import theme from '../assets/theme';
import { Grade } from '../components/_molecules/SettingsModalDesigner/SettingsModalDesigner';
import { mapGradeToGradeWithOrdinalIndicator } from '../components/dynamicPages/CreatePage/CreateFlow/components/GradeSelect';
import { EMPTY_SLATE_ELEMENT } from '../components/SlideComponents/slateUtil';
import { Slide, SlideType } from '../components/SlideComponents/slide.types';
import { createTextElement } from '../components/SlideComponents/slideUtil';
import {
  Response,
  Round,
  RoundMedia,
  RoundOptions,
  RoundType,
} from '../services/backendService/types';
import { generateUniqueId } from './general';

export const getDefaultResponsesCountByRoundType = (type: RoundType): number => {
  switch (type) {
    case 'questions':
    case 'answers':
    case 'wordcloud':
    case 'ideas':
      return 5;
    default:
      return 1;
  }
};

export const emptyNonPlayedRounds: Omit<Round, 'id' | 'type'> = {
  dataReferenceRoundId: null,
  durationSeconds: null,
  durationSecondsConfig: null,
  title: null,
};

export const createInteractiveSlideByType = ({
  type,
  hasVoting,
  votingTitle,
  title,
  roundOptions,
  wasAddedDuringLive,
  backgroundMedia,
  metadata,
  textColor,
}: {
  type: SlideType;
  hasVoting: boolean;
  title?: string | null;
  votingTitle?: string | null;
  roundOptions?: RoundOptions[] | null;
  wasAddedDuringLive: boolean | null;
  backgroundMedia: RoundMedia | null;
  metadata?: { criteria?: string; grade?: string | number | boolean };
  textColor?: string;
}): Slide[] => {
  const titleElement = createTextElement({
    lockedPosition: true,
    lockedFromDeletion: true,
    textValue: title || '',
    fontSize: 100,
    x: 1920 * 0.05,
    y: 1080 * 0.1,
    textColor,
  });

  const votingTitleElement = createTextElement({
    lockedPosition: true,
    lockedFromDeletion: true,
    textValue: votingTitle || '',
    fontSize: 100,
    x: 1920 * 0.05,
    y: 1080 * 0.1,
    textColor,
  });

  switch (type) {
    case 'answers':
    case 'canvas': {
      const id = generateUniqueId().toString();
      const newResponseSlide: Slide = {
        id,
        type,
        height: 1080,
        width: 1920,
        elements: [titleElement],
        contextElements: [{ elementId: titleElement.id, purpose: 'question' }],
        version: 1,
        backgroundMedia: backgroundMedia || undefined,
        durationSeconds: type === 'canvas' ? 300 : 60,
        durationSecondsConfig: type === 'canvas' ? 300 : 60,
        wasAddedDuringLive,
      };
      if (hasVoting) {
        const votingId = generateUniqueId().toString();
        const newVotingSlide: Slide = {
          id: votingId,
          type: 'voting',
          height: 1080,
          width: 1920,
          elements: [votingTitleElement],
          contextElements: [{ elementId: votingTitleElement.id, purpose: 'question' }],
          version: 1,
          backgroundMedia: backgroundMedia || undefined,
          durationSeconds: 60,
          durationSecondsConfig: 60,
          dataReferenceSlideId: id,
          wasAddedDuringLive,
        };
        const newPodiumSlide: Slide = {
          id: generateUniqueId().toString(),
          type: 'podium',
          height: 1080,
          width: 1920,
          elements: [],
          contextElements: [],
          version: 1,
          backgroundMedia: backgroundMedia || undefined,
          durationSeconds: null,
          durationSecondsConfig: null,
          dataReferenceSlideId: votingId,
          wasAddedDuringLive,
        };
        return [newResponseSlide, newVotingSlide, newPodiumSlide];
      } else {
        const newResponsegridSlide: Slide = {
          id: generateUniqueId().toString(),
          type: 'responsegrid',
          height: 1080,
          width: 1920,
          elements: [],
          contextElements: [],
          version: 1,
          backgroundMedia: backgroundMedia || undefined,
          durationSeconds: null,
          durationSecondsConfig: null,
          dataReferenceSlideId: id,
          wasAddedDuringLive,
        };
        return [newResponseSlide, newResponsegridSlide];
      }
    }

    case 'wordcloud': {
      const newWordcloudSlide: Slide = {
        id: generateUniqueId().toString(),
        type,
        height: 1080,
        width: 1920,
        responseLimit: getDefaultResponsesCountByRoundType('wordcloud'),
        elements: [titleElement],
        contextElements: [{ elementId: titleElement.id, purpose: 'question' }],
        version: 1,
        backgroundMedia: backgroundMedia || undefined,
        durationSeconds: 60,
        durationSecondsConfig: 60,
        wasAddedDuringLive,
      };
      return [newWordcloudSlide];
    }

    case 'poll': {
      const newPollSlide: Slide = {
        id: generateUniqueId().toString(),
        type,
        height: 1080,
        width: 1920,
        elements: [titleElement],
        contextElements: [{ elementId: titleElement.id, purpose: 'question' }],
        version: 1,
        backgroundMedia: backgroundMedia || undefined,
        durationSeconds: 60,
        durationSecondsConfig: 60,
        wasAddedDuringLive,
        pollOptions: roundOptions
          ? roundOptions
          : [
              {
                id: generateUniqueId().toString(),
                color: theme.colors.orange,
                displayName: JSON.stringify([{ ...EMPTY_SLATE_ELEMENT, size: 0.35 }]),
              },
              {
                id: generateUniqueId().toString(),
                color: theme.colors.green,
                displayName: JSON.stringify([{ ...EMPTY_SLATE_ELEMENT, size: 0.35 }]),
              },
              {
                id: generateUniqueId().toString(),
                color: theme.colors.green,
                displayName: JSON.stringify([{ ...EMPTY_SLATE_ELEMENT, size: 0.35 }]),
              },
            ],
      };
      return [newPollSlide];
    }

    case 'aiAssessedAnswers': {
      const id = generateUniqueId().toString();
      let grade = '5th';
      try {
        const homeData = localStorage.getItem('@curipod_v1_home_grade');
        if (homeData) {
          const { value } = JSON.parse(homeData) as { value: string };
          if (value) grade = mapGradeToGradeWithOrdinalIndicator(value as Grade);
        }
      } catch {
        /* ignore */
      }
      const newAiAssessedAnswersSlide: Slide = {
        id,
        type,
        height: 1080,
        width: 1920,
        elements: [titleElement],
        contextElements: [{ elementId: titleElement.id, purpose: 'question' }],
        version: 1,
        backgroundMedia: backgroundMedia || undefined,
        durationSeconds: 60 * 5,
        durationSecondsConfig: 60 * 5,
        wasAddedDuringLive,
        responseLimit: 1,
        metadata: {
          ...metadata,
          grade: grade,
          feedbackVariation: 'comprehensionFeedback',
        },
      };
      const newResponsegridSlide: Slide = {
        id: generateUniqueId().toString(),
        type: 'responsegrid',
        height: 1080,
        width: 1920,
        elements: [],
        contextElements: [],
        version: 1,
        backgroundMedia: backgroundMedia || undefined,
        durationSeconds: null,
        durationSecondsConfig: null,
        dataReferenceSlideId: id,
        wasAddedDuringLive,
      };
      return [newAiAssessedAnswersSlide, newResponsegridSlide];
    }

    default:
      return [];
  }
};

/**
 * Calculates an estimated number of lines the response will fill
 * Calculated using window width, number of columns the page is split into and length of response
 * Extra lines are added where there is a \n - new line in the response
 * @param textResponse a single text response
 * @param windowWidth current window size
 * @returns a number - the maximum number of lines the repsonse should fill
 */

export const calculateEstimatedAnswerLength = (
  textResponse: string,
  windowWidth: number,
) => {
  //average 13px per character at 24px font-size - based on nunito font
  const characterWidth = 13;
  const numberOfColumns =
    windowWidth <= 669 ? 1 : windowWidth <= 1019 ? 2 : windowWidth <= 1439 ? 3 : 4;
  const columnWidthWithoutGap =
    ((windowWidth - 20 * numberOfColumns) * 0.9) / numberOfColumns;
  const textAreaSizeWithoutPadding = columnWidthWithoutGap - 40;
  const responseLength = textResponse.length;
  const estimatedNumberOfLines =
    responseLength / (textAreaSizeWithoutPadding / characterWidth);
  const numberOfNewLines = textResponse.split(/\n/).length;
  return Math.ceil(estimatedNumberOfLines + (numberOfNewLines - 1));
};

export const calculateEstimatedColumnLength = (
  column: Response[],
  windowWidth: number,
) => {
  let columnLength = 0;

  column.forEach(response => {
    columnLength += calculateEstimatedAnswerLength(response.response, windowWidth);
  });
  return columnLength;
};

export const findShortestColumn = (
  columns: {
    columnOne: Response[];
    columnTwo: Response[];
    columnThree: Response[];
    columnFour: Response[];
  },
  windowWidth: number,
) => {
  const numberOfColumns =
    windowWidth <= 669 ? 1 : windowWidth <= 1019 ? 2 : windowWidth <= 1439 ? 3 : 4;
  const columnOneLength = calculateEstimatedColumnLength(columns.columnOne, windowWidth);
  const columnTwoLength = calculateEstimatedColumnLength(columns.columnTwo, windowWidth);
  const columnThreeLength = calculateEstimatedColumnLength(
    columns.columnThree,
    windowWidth,
  );
  const columnFourLength = calculateEstimatedColumnLength(
    columns.columnFour,
    windowWidth,
  );

  const columnLengths = [
    columnOneLength,
    columnTwoLength,
    columnThreeLength,
    columnFourLength,
  ];

  const maxColumns = columnLengths.slice(0, numberOfColumns);
  const shotestLength = Math.min(...maxColumns);
  const shortestColumn = maxColumns.indexOf(shotestLength) + 1;
  return shortestColumn;
};
